import { cn } from '../../../../lib/utils';
import React from 'react';
import { ProcessingIndicatorLoadingSpinner } from '../../../components/overlays';

export interface ProgressIndicatorProps extends React.ComponentProps<'div'> {
  /** 프로그레스 인디케이터 타입 */
  type?: 'linear' | 'circular';
  /** 프로그레스 인디케이터 크기 */
  size?: 'large' | 'small';
  /** 프로그레스 인디케이터 값 */
  value: number;
  /** 프로그레스 인디케이터 단위 */
  unit?: React.ReactNode;
  /** 프로그레스 인디케이터 완료 노드 */
  completedNode?: React.ReactNode;
  /** 프로그레스 인디케이터 클래스네임 */
  className?: string;
}

export default function ProgressIndicator({
  type = 'linear',
  size = 'large',
  value,
  unit,
  completedNode,
  className,
  ...rest
}: ProgressIndicatorProps) {
  if (type === 'linear') {
    return (
      <LinearProgressIndicator
        value={value}
        size={size}
        unit={unit}
        completedNode={completedNode}
        className={className}
        {...rest}
      />
    );
  } else {
    return (
      <CircularProgressIndicator
        value={value}
        size={size}
        unit={unit}
        completedNode={completedNode}
        className={className}
        {...rest}
      />
    );
  }
}

interface LinearProgressIndicatorProps {
  value: number;
  size?: 'large' | 'small';
  className?: string;
  unit?: React.ReactNode;
  completedNode?: React.ReactNode;
}

const LinearProgressIndicator: React.FC<LinearProgressIndicatorProps> &
  React.ComponentProps<'div'> = ({
  value,
  size,
  unit,
  completedNode,
  className,
  ...rest
}) => {
  const height = size === 'large' ? '8px' : '4px';

  return (
    <div
      className={cn(
        'flex w-full items-center justify-center gap-4xs',
        className,
      )}
      {...rest}
    >
      {/* 프로그레스 바 */}
      <div
        className={cn(
          'flex-1 overflow-hidden rounded-full bg-comp-progress-track-bg',
          `h-[${height}]`,
        )}
      >
        <div
          className={cn(
            'h-full rounded-full bg-comp-progress-fill transition-all duration-300 ease-out',
          )}
          style={{
            width: `${value}%`,
          }}
        />
      </div>
      {completedNode && value >= 100 && completedNode}
      {(!completedNode || (completedNode && value < 100)) && unit && (
        <div className="flex items-center text-comp-accent-xsmall text-comp-progress-text">
          {value}
          {unit}
        </div>
      )}
    </div>
  );
};

interface CircularProgressIndicatorProps {
  value: number;
  size?: 'large' | 'small';
  className?: string;
  unit?: React.ReactNode;
  completedNode?: React.ReactNode;
}

const CircularProgressIndicator: React.FC<CircularProgressIndicatorProps> &
  React.ComponentProps<'div'> = ({
  value,
  size = 'large',
  unit,
  completedNode,
  ...rest
}) => {
  if (completedNode && value >= 100) {
    return completedNode;
  }

  const sizeMap = {
    large: 60,
    small: 20,
  };

  const strokeWidthMap = {
    large: 6,
    small: 2,
  };

  return (
    <div
      className={cn(
        size === 'large' && 'size-[64px]',
        size === 'small' && 'size-[24px]',
        'p-[2px]',
      )}
    >
      <ProcessingIndicatorLoadingSpinner
        value={value}
        size={sizeMap[size]}
        strokeWidth={strokeWidthMap[size]}
        showPercentage={!!unit && size === 'large'}
        percentageFontSize={12}
        percentageClassName="text-comp-accent-xsmall"
        {...rest}
      />
    </div>
  );
};
